import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useSearchParams } from 'react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch } from 'store/store';
import { useNavigate, useUncobrandedPathName } from 'features/cobrand/hooks/useCobrandUtils';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { setNavigationTab } from '../navigationSlice';
import { desktopNavbarItems, tabletMobileNavbarItems } from '../navbarItems';
import { NavbarItem } from '../types';

export const useRoutesState = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const pathname = useUncobrandedPathName();
  const [searchParams] = useSearchParams();
  const { isMobile, isTablet, isDesktop } = useDeviceType();
  const { translation } = useTranslationContext();
  const section = useMemo(() => searchParams.get('section'), [searchParams]);
  const updateHomeRouteBasedOnScreenSize = useCallback(() => {
    if (pathname === '/home' && !section) {
      if (isMobile || isTablet) {
        dispatch(
          setNavigationTab({
            route: '/home?section=checklist',
            label: translation('checklist_tab_text'),
            selectedTabValue: NavbarItem.CHECKLIST,
          }),
        );
        navigate('/home?section=checklist');
      }
    } else if (pathname === '/home' && section) {
      if (isDesktop) {
        dispatch(
          setNavigationTab({
            route: '/home',
            label: translation('home'),
            selectedTabValue: NavbarItem.HOME,
          }),
        );
        navigate('/home');
      }
    }
  }, [pathname, isMobile, isTablet, isDesktop, section]);

  useEffect(() => {
    if (pathname === '/home') {
      // Check for device size on home page route and update to correct route
      updateHomeRouteBasedOnScreenSize();
    }
  }, [isMobile, isTablet, isDesktop]);
  const initialiseNavigationTab = useCallback(() => {
    const route = section ? `${pathname}?section=${section}` : pathname;
    const navbarItems = isDesktop ? desktopNavbarItems : tabletMobileNavbarItems;
    // Exact match first (handles routes with a `section` query param, e.g. /home?section=checklist);
    // fall back to a path-prefix match so subpages (e.g. /resources/topics) keep their parent tab selected.
    const selectedTab =
      navbarItems.find((item) => item.route === route) ??
      navbarItems.find((item) => pathname.startsWith(`${item.route}/`));

    if (!selectedTab) {
      dispatch(setNavigationTab(null));
      return;
    }

    dispatch(
      setNavigationTab({
        route: selectedTab.route,
        label: selectedTab.label,
        selectedTabValue: selectedTab.tabValue,
      }),
    );
  }, [isDesktop, pathname, section, isMobile, isTablet]);
  // Use useEffect to listen to location changes and update the tab when the route changes (including back navigation)
  useEffect(() => {
    initialiseNavigationTab();
  }, [pathname, section, isMobile, isTablet, isDesktop, initialiseNavigationTab]);

  return { initialiseNavigationTab };
};
